import React, { useState, useEffect, forwardRef } from 'react';
import { Fab, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';

const PULSE_KEY = 'supportFabPulsed';

interface SupportFABProps {
  onClick: () => void;
  isOpen: boolean;
}

const MotionFab = motion.create(
  forwardRef<HTMLButtonElement, any>((props, ref) => <Fab ref={ref} {...props} />)
);

const SupportFAB = forwardRef<HTMLButtonElement, SupportFABProps>(
  ({ onClick, isOpen }, ref) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const shouldReduceMotion = useReducedMotion();
    const [shouldPulse, setShouldPulse] = useState(false);

    useEffect(() => {
      if (shouldReduceMotion) return;
      const hasPulsed = sessionStorage.getItem(PULSE_KEY);
      if (!hasPulsed) {
        setShouldPulse(true);
        sessionStorage.setItem(PULSE_KEY, 'true');
      }
    }, [shouldReduceMotion]);

    return (
      <Tooltip title="Need help?" placement="left" arrow>
        <MotionFab
          ref={ref}
          color="primary"
          size={isMobile ? 'medium' : 'large'}
          onClick={onClick}
          aria-label="Open support form"
          aria-expanded={isOpen}
          data-spotlight-id="support-fab"
          initial={
            shouldPulse && !shouldReduceMotion
              ? { scale: 1 }
              : false
          }
          animate={
            shouldPulse && !shouldReduceMotion
              ? {
                  scale: [1, 1.15, 1],
                  boxShadow: [
                    '0 3px 5px -1px rgba(0,0,0,.2), 0 6px 10px 0 rgba(0,0,0,.14), 0 1px 18px 0 rgba(0,0,0,.12)',
                    `0 0 20px 4px ${theme.palette.primary.main}40`,
                    '0 3px 5px -1px rgba(0,0,0,.2), 0 6px 10px 0 rgba(0,0,0,.14), 0 1px 18px 0 rgba(0,0,0,.12)',
                  ],
                }
              : {}
          }
          transition={
            shouldPulse
              ? { duration: 0.6, ease: 'easeInOut', delay: 1 }
              : undefined
          }
          whileHover={shouldReduceMotion ? undefined : { scale: 1.08 }}
          whileTap={shouldReduceMotion ? undefined : { scale: 0.95 }}
          onAnimationComplete={() => {
            setShouldPulse(false);
          }}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 16 : 24,
            right: isMobile ? 16 : 24,
            zIndex: 1300,
          }}
        >
          <HelpOutline />
        </MotionFab>
      </Tooltip>
    );
  }
);

SupportFAB.displayName = 'SupportFAB';

export default SupportFAB;
